import React from "react";
import html2canvas from "html2canvas-pro";
import { Document, Image, Page, StyleSheet, pdf } from "@react-pdf/renderer";
import { METRICS_PDF_PAGE_SIZE } from "../../miContenido/metricsPdf/constants";
import { REPORT_WEB } from "../../miContenido/metricsReportWeb/reportWebTheme";
import { downloadBlob } from "../../utils/programAssetPngExport";

export const METRICS_REPORT_WEB_ROOT_ID = "metrics-report-web-root";
export const METRICS_REPORT_WEB_PAGE_SELECTOR = "[data-metrics-report-web-page]";

const SLIDE_W = REPORT_WEB.pageWidthPx;
const SLIDE_H = REPORT_WEB.pageMinHeightPx;
const PDF_W = METRICS_PDF_PAGE_SIZE[0];
const PDF_H = METRICS_PDF_PAGE_SIZE[1];
// Raster export: text is a bitmap, so DPI is the lever. 1280pt page * 3 =
// 3840px ≈ 216 DPI. Going higher (4x+) overflows browser canvas/memory limits
// and causes pages to drop, so 3x is the safe ceiling for this path.
const CAPTURE_SCALE = 3;
const CAPTURE_SHELL_ID = "metrics-report-web-capture-shell";

const CAPTURE_FONT_LOAD_SPECS = [
  '400 12px "DM Sans"',
  '500 12px "DM Sans"',
  '600 12px "DM Sans"',
  '700 16px "DM Sans"',
  '700 30px "DM Sans"',
  '700 56px "DM Sans"',
];

const slidePdfStyles = StyleSheet.create({
  page: {
    padding: 0,
    backgroundColor: "#FFFFFF",
  },
  slide: {
    width: PDF_W,
    height: PDF_H,
  },
});

function waitForLayout(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}

function preparePageForCapture(page: HTMLElement): void {
  page.style.boxShadow = "none";
  page.style.borderRadius = "0";
  page.style.margin = "0";
  page.style.width = `${SLIDE_W}px`;
  page.style.height = `${SLIDE_H}px`;
  page.style.maxWidth = `${SLIDE_W}px`;
  page.style.minHeight = `${SLIDE_H}px`;
  page.style.flexShrink = "0";
  page.style.setProperty("-webkit-font-smoothing", "antialiased");

  page.querySelectorAll<HTMLElement>("*").forEach((el) => {
    el.style.setProperty("-webkit-font-smoothing", "antialiased");
  });
}

function createCaptureShell(): HTMLDivElement {
  const existing = document.getElementById(CAPTURE_SHELL_ID);
  existing?.remove();

  const shell = document.createElement("div");
  shell.id = CAPTURE_SHELL_ID;
  Object.assign(shell.style, {
    position: "fixed",
    left: "0",
    top: "0",
    width: `${SLIDE_W}px`,
    height: `${SLIDE_H}px`,
    zIndex: "-1",
    opacity: "0",
    pointerEvents: "none",
    overflow: "hidden",
    background: "#ffffff",
  });
  document.body.appendChild(shell);
  return shell;
}

function removeCaptureShell(shell: HTMLDivElement | null): void {
  shell?.remove();
}

async function ensureCaptureFontsReady(): Promise<void> {
  if (typeof document === "undefined" || !document.fonts) return;
  try {
    await Promise.all(
      CAPTURE_FONT_LOAD_SPECS.map((spec) =>
        document.fonts.load(spec).catch(() => undefined),
      ),
    );
    await document.fonts.ready;
  } catch {
    // Best-effort.
  }
}

async function capturePageToCanvas(page: HTMLElement): Promise<HTMLCanvasElement> {
  preparePageForCapture(page);

  return html2canvas(page, {
    scale: CAPTURE_SCALE,
    useCORS: true,
    allowTaint: true,
    backgroundColor: "#ffffff",
    logging: false,
    width: SLIDE_W,
    height: SLIDE_H,
    windowWidth: SLIDE_W,
    windowHeight: SLIDE_H,
    scrollX: 0,
    scrollY: 0,
  });
}

async function composeSlidesToPdfBlob(
  canvases: HTMLCanvasElement[],
): Promise<Blob> {
  const element = (
    <Document>
      {canvases.map((canvas, index) => (
        <Page
          key={`slide-${index}`}
          size={METRICS_PDF_PAGE_SIZE}
          wrap={false}
          style={slidePdfStyles.page}
        >
          <Image
            src={canvas.toDataURL("image/png")}
            style={slidePdfStyles.slide}
          />
        </Page>
      ))}
    </Document>
  );
  const blob = await pdf(element).toBlob();
  if (!blob) {
    throw new Error("Failed to generate PDF blob");
  }
  return blob;
}

export function metricsReportWebPdfFilename(
  label: string,
  startDate: string,
  endDate: string,
): string {
  const slug = label
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 48);
  return `reporte-metricas-${slug || "reporte"}-${startDate}-${endDate}.pdf`;
}

export async function captureMetricsReportWebToPdfBlob(
  root: HTMLElement,
): Promise<Blob | null> {
  const sourcePages = Array.from(
    root.querySelectorAll<HTMLElement>(METRICS_REPORT_WEB_PAGE_SELECTOR),
  );
  if (sourcePages.length === 0) return null;

  await ensureCaptureFontsReady();

  const shell = createCaptureShell();
  const canvases: HTMLCanvasElement[] = [];

  try {
    for (const sourcePage of sourcePages) {
      const page = sourcePage.cloneNode(true) as HTMLElement;
      shell.replaceChildren(page);
      await waitForLayout();
      canvases.push(await capturePageToCanvas(page));
    }
  } finally {
    removeCaptureShell(shell);
  }

  return composeSlidesToPdfBlob(canvases);
}

export async function downloadMetricsReportWebPdf(args: {
  root: HTMLElement;
  label: string;
  startDate: string;
  endDate: string;
}): Promise<void> {
  const blob = await captureMetricsReportWebToPdfBlob(args.root);
  if (!blob) {
    throw new Error("Metrics report web pages not found");
  }
  downloadBlob(
    blob,
    metricsReportWebPdfFilename(args.label, args.startDate, args.endDate),
  );
}
